import { useState } from "react";
import { useAuthUser } from "@/lib/auth-context";
import { useCourseIndex } from "@/lib/course";
import { AppTopbar } from "@/components/AppTopbar";
import { CoursesCatalogDrawer } from "@/components/CoursesCatalogDrawer";
import { CoachNotesCard } from "@/components/CoachNotesCard";
import { CourseCardForSlug } from "@/components/CourseCard";
import { ResumeHero } from "@/components/home/ResumeHero";
import { useStreak } from "@/lib/streak";
import { useActivitySummary } from "@/lib/activity";
import { FlameIcon } from "@/components/icons";

export default function Home() {
  const { user, isLoaded } = useAuthUser();
  const index = useCourseIndex();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const streak = useStreak();
  const activity = useActivitySummary();
  if (!isLoaded || !user) return null;
  const displayName = (user.firstName ?? "").trim();

  return (
    <>
      <AppTopbar onOpenDrawer={() => setDrawerOpen(true)} />
      <CoursesCatalogDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <main className="p-4 sm:p-8 max-w-5xl mx-auto">
        <p className="text-muted-foreground text-sm mb-1">
          {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
        </p>
        <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl mb-8">
          Welcome back{displayName ? `, ${displayName}` : ""} 👋
        </h1>
        {streak.status === "ready" && streak.streak.dayStreak >= 2 && (
          <p className="text-sm text-muted-foreground -mt-6 mb-8 flex items-center gap-1">
            <FlameIcon className="h-3.5 w-3.5 text-gold" />
            {`${streak.streak.dayStreak}-day streak`}
            {activity.status === "ready" && activity.summary.lessonsCompletedThisWeek > 0
              ? ` · ${activity.summary.lessonsCompletedThisWeek} lessons this week`
              : ""}
          </p>
        )}

        <ResumeHero />

        <CoachNotesCard />

        {index.status === "loading" && (
          <div className="space-y-4">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                role="status"
                aria-live="polite"
                className="rounded-card border border-border bg-muted/30 h-20 motion-safe:animate-pulse"
              >
                <span className="sr-only">Loading courses</span>
              </div>
            ))}
          </div>
        )}
        {index.status === "error" && (
          <p className="text-destructive">Failed to load courses: {index.message}</p>
        )}
        {index.status === "ready" && index.courses.length === 0 && (
          <p className="text-muted-foreground">No courses available yet.</p>
        )}
        {index.status === "ready" && index.courses.length > 0 && (
          <div className="space-y-4">
            {index.courses.map((c) => (
              <CourseCardForSlug key={c.slug} courseSlug={c.slug} />
            ))}
          </div>
        )}
      </main>
    </>
  );
}
